import { Link } from 'react-router';
import { COMPANY } from '@/content/company';
import { Clause, LegalPage } from './LegalPage';
import legal from './LegalPage.module.css';

/**
 * Cookie Policy.
 *
 * This page once documented an `sk_session` cookie that did not exist, and was
 * rewritten to state the stronger, accurate position: no cookies at all.
 *
 * It has now been rewritten a second time, in the other direction. Adding
 * reCAPTCHA v3 to the contact form means Google scripts and Google cookies on
 * that one route, so "no cookies of any kind" became false the moment it
 * shipped. The claim is narrowed to what remains true - every route except
 * /contact - rather than quietly left standing.
 *
 * The rule this page has been through twice: a policy that describes something
 * other than what the site does is the easiest kind of claim for a regulator, a
 * payment provider or a client's procurement team to falsify. Whichever
 * direction the error runs.
 *
 * If the captcha is ever removed, or moved behind consent, this page changes in
 * the same commit.
 *
 * THIRD REWRITE, and the reason is worth recording because the first two nearly
 * repeated themselves. The page claimed - in four places, including a paragraph
 * inviting the reader to check in their own devtools - that nothing is written to
 * local storage on any page. Measured in a real browser, reCAPTCHA writes a
 * first-party `_grecaptcha` entry into local storage under skabro.eu the moment a
 * token is minted, which is to say whenever anyone submits the form or asks for
 * the phone number. Not on page load, and not on any other route.
 *
 * The cookie exception had been carved out correctly; the storage exception was
 * missed because it is easy to assume a third-party script only writes to its own
 * origin. § 2 of this page states that the ePrivacy rules cover local storage
 * exactly as they cover cookies, so getting this wrong contradicted the page's own
 * statement of the law.
 *
 * The lesson is the one from the first two rewrites, one level down: when
 * verifying a "we store nothing" claim, check cookies AND local storage AND
 * session storage, on the route that loads the third party, AFTER exercising the
 * thing that triggers it. Checking on page load says nothing about what a submit
 * does.
 */

const TOC = [
  '1. Summary',
  '2. What cookies are',
  '3. What is stored',
  '4. reCAPTCHA',
  '5. What we do not do',
  '6. If this changes',
  '7. Managing cookies & storage',
] as const;

export function Cookies() {
  return (
    <LegalPage
      eyebrow="Cookies & consent"
      title="Cookie Policy"
      toc={TOC}
      intro={
        <>
          skabro.eu sets no cookies of its own, ever, on any page. Eight of its nine pages store
          nothing on your device at all and load nothing from anyone else&rsquo;s servers. The
          contact page is the exception: it uses Google reCAPTCHA to keep automated abuse away from
          the enquiry form and from our telephone number, and doing so both lets Google set a cookie
          in its own domain and writes one reCAPTCHA entry into your browser&rsquo;s local storage.
          This page explains exactly where that line falls.
        </>
      }
    >
      <Clause n={1} title="The short version">
        <p>
          Browsing this site sets <strong>no cookies</strong> - not analytics, not advertising, not
          necessary ones. On eight of the nine pages nothing whatsoever is written to your device,
          and the fonts, styles, scripts and images all come from skabro.eu itself.
        </p>
        <p>
          <strong>One page differs.</strong> On <Link to="/contact">/contact</Link> we load Google
          reCAPTCHA, so that neither the enquiry form nor our telephone number can be used by bots.
          That fetches code from Google, lets Google set a cookie of its own - in its domain, not
          ours - and, when you actually send the form or ask for the number, writes one reCAPTCHA
          entry into local storage. It happens only on that page, only because those two things are
          there, and it is described in full in section 4.
        </p>
        <p>
          There is no cookie banner. reCAPTCHA is used strictly to protect a service you are asking
          us for, which is the one category that does not require prior consent - see section 6 for
          why that position is not entirely free of doubt.
        </p>
      </Clause>

      <Clause n={2} title="What cookies are">
        <p>
          Cookies are small text files a website stores on your device, usually to remember
          something between page loads. They can be strictly necessary for a service you asked for,
          or optional - analytics, personalisation, advertising. Under Slovak Act No. 452/2021 Coll.
          on electronic communications, which implements the EU ePrivacy Directive, optional cookies
          may only be stored with your prior consent.
        </p>
        <p>
          The same rules apply to any other technology that stores or reads information on your
          device, including local storage, session storage and tracking pixels.
        </p>
      </Clause>

      <Clause n={3} title="What is stored on your device">
        <p className={legal.continues}>
          <strong>skabro.eu sets no cookies at all</strong>, on any page, including the contact
          form. Nothing here is ours, and nothing here is used to recognise or follow you. What the
          reCAPTCHA on <Link to="/contact">/contact</Link> does store, in full, is this:
        </p>
        <ul>
          <li>
            <strong>
              <code>_GRECAPTCHA</code>
            </strong>{' '}
            <em>(cookie)</em> - set by <em>Google</em>, not by us, and stored against{' '}
            <code>google.com</code> rather than <code>skabro.eu</code>. reCAPTCHA loads inside a
            frame served by Google, and the cookie belongs to that frame. It helps reCAPTCHA tell a
            returning browser from a fresh one, which is part of how it scores a submission.
            Typically persists around six months. We can neither read it nor delete it.
          </li>
          {/*
           * Named separately from the cookie above because it lives on a
           * different origin, and that difference is the whole reason it was
           * missed once. It is first-party storage - the reader's devtools show
           * it under skabro.eu - so a policy that mentioned only the Google-domain
           * cookie would be describing the smaller half of what happens.
           */}
          <li>
            <strong>
              <code>_grecaptcha</code>
            </strong>{' '}
            <em>(local storage)</em> - written by Google&rsquo;s reCAPTCHA code, but stored under{' '}
            <code>skabro.eu</code>, because that is the page the code runs on. It appears when a
            check actually runs - that is, when you send the enquiry form or press{' '}
            <em>Show phone number</em> - and not merely from opening the page. It holds a reCAPTCHA
            token value, it is not an identifier we set, read or use, and it persists until you
            clear your browser storage. We do not put anything of our own beside it.
          </li>
        </ul>
        <p>
          Nothing is written to <strong>session storage</strong>, on any page. Nor does anything of
          ours survive a page load: even the telephone number, once you have asked for it, is held
          in memory for that visit only and is gone when you close the tab.
        </p>
        <p>
          Because it is a third-party cookie, many browsers block it outright - Safari and Firefox
          by default, Chrome depending on your settings. reCAPTCHA still works when they do.
        </p>
        <p>
          Google may also read cookies it has already set in your browser from other sites. That is
          outside our control and is a consequence of using their service, which is why section 4
          exists.
        </p>
        {/*
         * This paragraph is the reason the omission above mattered so much. It
         * invites the reader to run the check themselves, which means any
         * inaccuracy in it is not merely wrong but demonstrably wrong, by the
         * method we handed them. It now describes what they will actually find,
         * including the order of events - the storage entry does not exist until
         * a check runs, so a reader who only loads the page and looks would
         * otherwise conclude we had invented it.
         */}
        <p>
          You can confirm all of this rather than taking our word for it. Open your browser&rsquo;s
          developer tools, look under Application or Storage, and browse the site: under{' '}
          <code>skabro.eu</code> the cookie list stays empty on every page, and on eight of the nine
          pages local storage and session storage are empty too. Then open /contact and send the
          form or press <em>Show phone number</em>: a single <code>_grecaptcha</code> entry appears
          in local storage, and a <code>google.com</code> cookie entry alongside it. Both are the
          ones described above, and they are the only things you will find. The network panel shows
          requests to skabro.eu only, except on /contact, where <code>google.com</code> and{' '}
          <code>gstatic.com</code> also appear.
        </p>
      </Clause>

      <Clause n={4} title="Google reCAPTCHA on the contact page">
        <p className={legal.continues}>
          The contact form is a public endpoint, and a public endpoint attracts automated abuse. We
          defend it in several ways that involve nobody else - a hidden field bots fill in and
          humans cannot see, submission rate limits, and a daily ceiling. The same check also guards
          the button that shows our telephone number, which is kept off the page precisely so it
          cannot be harvested in bulk. reCAPTCHA v3 is the one layer in any of this that requires a
          third party. When you open the contact page and when you use either of those two things:
        </p>
        <ul>
          <li>
            Your browser loads <code>recaptcha/api.js</code> from <code>google.com</code> and{' '}
            <code>gstatic.com</code>, which in turn opens a hidden frame served by Google. Those two
            hosts are the only third-party requests this site makes anywhere.
          </li>
          <li>
            reCAPTCHA collects information about your browser, device and interaction with the page,
            and sends it to Google, which returns a score for how likely the request is to be
            automated.
          </li>
          <li>
            At that point Google&rsquo;s code writes its <code>_grecaptcha</code> entry into local
            storage under <code>skabro.eu</code>, and sets its <code>_GRECAPTCHA</code> cookie in{' '}
            <code>google.com</code>. Both are listed in section 3. Neither is ours, and we use
            neither.
          </li>
          <li>
            Our server checks that score before accepting your message, or before sending you the
            phone number. For an enquiry it is stored alongside the message so we can tell a false
            rejection from a real one; for the phone number it is kept for 24 hours with a hashed
            form of your IP address, purely to limit how often the number can be requested, and then
            deleted. Our <Link to="/privacy">Privacy Policy</Link> sets both out in full.
          </li>
          <li>
            <strong>Google is the recipient of that data</strong>, acting for its own purposes as
            well as ours, and may process it outside the EEA. Google&rsquo;s handling is governed by
            its own{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            , not by ours.
          </li>
        </ul>
        <p>
          We rely on our legitimate interest in preventing abuse of the form, and in keeping our
          telephone number out of bulk-harvested lists, as the legal basis - and we have limited the
          exposure as far as the service allows: it loads on one route out of nine, never on a page
          you are only reading, and it is not used for analytics, advertising or measurement of any
          kind.
        </p>
        <p>
          <strong>If you would rather not involve Google</strong>, write to{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> instead. It reaches the same inbox
          as the form and no captcha is involved. We would rather you contacted us than submitted to
          a tracker you object to.
        </p>
      </Clause>

      <Clause n={5} title="What we deliberately do not use">
        <p className={legal.continues}>
          Some of this is a design decision rather than an obligation, so it is worth being
          specific. This site has no:
        </p>
        <ul>
          <li>
            <strong>Analytics.</strong> No Google Analytics, no Plausible, no Matomo, no self-hosted
            analytics of any kind. We do not count you.
          </li>
          <li>
            <strong>Advertising or remarketing.</strong> No ad networks, no conversion pixels, no
            retargeting tags.
          </li>
          <li>
            <strong>Social media embeds.</strong> No share widgets, no embedded feeds, no comment
            systems - all of which load third-party code and set third-party cookies.
          </li>
          <li>
            <strong>Externally hosted assets.</strong> Fonts are served from this domain rather than
            from a font CDN, which is a common and often overlooked way of disclosing visitor IP
            addresses to a third party.
          </li>
          <li>
            <strong>Fingerprinting of our own.</strong> We make no attempt to identify your device
            or browser. reCAPTCHA does profile your browser in order to score it, which is the
            trade-off described in section 4, and it is confined to the contact form.
          </li>
        </ul>
        <p>
          Our hosting provider records standard server log data in order to serve and secure the
          site. That is not a cookie and it is not stored on your device; it is covered in our{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </Clause>

      <Clause n={6} title="If this ever changes">
        <p>
          Should we add anything else that stores data on your device, this page will be updated
          with a new date <em>before</em> the change goes live, so what you read here is never
          behind what the site does.
        </p>
        <p>
          Anything optional, analytics included, would run only after you had actively given
          consent, and a consent control would appear on the site at that point.
        </p>
        <p>
          In fairness, the position in section 1 deserves a caveat. Treating an anti-abuse captcha
          as strictly necessary, and so exempt from prior consent, is the common reading and the one
          we rely on - but it is not settled, and some European data protection authorities take a
          narrower view of reCAPTCHA specifically because Google also benefits from the data. If
          that reading prevails we will either put the captcha behind a consent step or replace it
          with one that sets no cookies. We would rather write that down than leave you to discover
          the argument exists.
        </p>
      </Clause>

      <Clause n={7} title="Managing cookies and storage in your browser">
        <p>
          Every major browser lets you view, block and delete cookies and site storage from its
          privacy settings. Deleting the two entries in section 3 is safe: reCAPTCHA simply writes a
          fresh one the next time a check runs, and nothing on the site depends on either.
        </p>
        {/*
         * The old wording here said blocking cookies "may" stop the form
         * submitting, which sat awkwardly against section 3 saying reCAPTCHA
         * still works when third-party cookies are blocked. Both were roughly
         * true of different settings, which is exactly how a reader ends up
         * unsure which to believe. Split by what is actually being blocked.
         */}
        <p>
          Blocking <em>third-party</em> cookies - the default in Safari and Firefox - changes
          nothing here: reCAPTCHA still works, as section 3 says. Blocking scripts or storage for{' '}
          <code>skabro.eu</code> itself, or blocking <code>google.com</code> outright, is different:
          the captcha then cannot produce a result, and the form and the phone reveal will refuse
          rather than let an unchecked request through. If you browse that way, email us at{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> - it reaches the same inbox and no
          captcha is involved.
        </p>
        <p>
          Questions about this policy can be sent to{' '}
          <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
        </p>
      </Clause>
    </LegalPage>
  );
}
